import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CalendarIcon, Users, Clock, Check } from "lucide-react";
import { Link } from "react-router-dom";

const DAY_KEYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

const fmtDate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// Generate 15-min slots within an interval (start, end as "HH:MM"), stop 30 min before end
function genRange(start, end) {
  if (!start || !end) return [];
  const out = [];
  let [sh, sm] = start.split(":").map(Number);
  let [eh, em] = end.split(":").map(Number);
  // last bookable slot = end - 30 min
  let endMins = eh * 60 + em - 30;
  let curMins = sh * 60 + sm;
  while (curMins <= endMins) {
    out.push(`${String(Math.floor(curMins / 60)).padStart(2,"0")}:${String(curMins % 60).padStart(2,"0")}`);
    curMins += 15;
  }
  return out;
}

export default function Reservation() {
  const [form, setForm] = useState({ firstname: "", phone: "", email: "", time: "", guests: 2, comment: "", marketing_optin: false, website: "" });
  const [date, setDate] = useState(undefined);
  const [closedDates, setClosedDates] = useState([]);
  const [hours, setHours] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => {
      setClosedDates(data.closed_dates || []);
      setHours(data.hours_reservation || data.hours || {});
    }).catch(() => {});
  }, []);

  const disabledMatcher = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (d.getDay() === 2) return true; // Tuesday
    if (closedDates.includes(fmtDate(d))) return true;
    return false;
  };

  const slots = useMemo(() => {
    if (!date || !hours) return [];
    const dayKey = DAY_KEYS[date.getDay()];
    const h = hours[dayKey];
    if (!h || h.closed) return [];
    const all = [...genRange(h.lunch_open, h.lunch_close), ...genRange(h.dinner_open, h.dinner_close)];
    // 30-min lead time
    const now = new Date();
    const minTime = new Date(now.getTime() + 30 * 60 * 1000);
    const isToday = fmtDate(date) === fmtDate(now);
    return all.filter((s) => {
      if (!isToday) return true;
      const [hh, mm] = s.split(":").map(Number);
      const dt = new Date(date); dt.setHours(hh, mm, 0, 0);
      return dt >= minTime;
    });
  }, [date, hours]);

  // auto-select first available slot when slots change
  useEffect(() => {
    if (slots.length > 0 && !slots.includes(form.time)) {
      setForm((f) => ({ ...f, time: slots[Math.min(slots.length - 1, 12)] || slots[0] }));
    }
    if (slots.length === 0 && form.time) {
      setForm((f) => ({ ...f, time: "" }));
    }
  }, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstname || !form.phone || !form.email || !date || !form.time) {
      toast.error("Merci de remplir tous les champs");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reservations", { ...form, date: fmtDate(date) });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la réservation");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="pt-28 pb-20 min-h-[80vh] bg-background" data-testid="reservation-success">
        <div className="max-w-xl mx-auto px-6 text-center fade-up">
          <div className="w-16 h-16 bg-primary text-white rounded-full mx-auto flex items-center justify-center mb-6">
            <Check size={32} />
          </div>
          <p className="uppercase tracking-[0.3em] text-xs text-primary mb-3">Réservation confirmée</p>
          <h1 className="font-display text-5xl text-foreground mb-4">À très vite, {form.firstname} !</h1>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            Votre table est <strong>confirmée</strong> pour le <strong>{fmtDate(date)}</strong> à <strong>{form.time}</strong> pour <strong>{form.guests} personne{form.guests > 1 ? "s" : ""}</strong>.<br />Un email de confirmation vient de vous être envoyé.
          </p>
          <Link to="/" className="bg-primary text-white px-6 py-3 rounded-sm hover:bg-primary/90 inline-block">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reservation-page" className="pt-28 pb-20 bg-background min-h-screen">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-xs text-primary mb-4">Réservation</p>
          <h1 className="font-display text-5xl lg:text-6xl text-foreground mb-4">Réserver une table</h1>
          <p className="text-muted-foreground text-lg max-w-md mx-auto">
            Choisissez votre date et l'horaire qui vous convient.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white border border-border rounded-sm p-8 lg:p-10 space-y-6">
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} aria-hidden="true" />

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Prénom *</Label>
            <Input data-testid="resa-firstname" value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} className="rounded-sm h-12" required />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Téléphone *</Label>
              <Input data-testid="resa-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm h-12" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Email *</Label>
              <Input data-testid="resa-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-12" required />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><CalendarIcon size={12} /> Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="resa-date-trigger" className="h-12 w-full px-3 border border-input bg-white rounded-sm text-left text-sm flex items-center justify-between hover:border-primary transition">
                    <span className={date ? "text-foreground" : "text-muted-foreground"}>{date ? fmtDate(date) : "Choisir"}</span>
                    <CalendarIcon size={14} className="text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={disabledMatcher}
                    initialFocus
                    data-testid="resa-calendar"
                  />
                  <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground">Mardi · congés indisponibles</div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Clock size={12} /> Heure *</Label>
              <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })} disabled={!date || slots.length === 0}>
                <SelectTrigger data-testid="resa-time" className="h-12 rounded-sm">
                  <SelectValue placeholder={!date ? "Choisir la date" : slots.length === 0 ? "Aucun créneau" : "Choisir"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {slots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Users size={12} /> Personnes *</Label>
              <Select value={String(form.guests)} onValueChange={(v) => setForm({ ...form, guests: Number(v) })}>
                <SelectTrigger data-testid="resa-guests" className="h-12 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "personne" : "personnes"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {date && slots.length === 0 && (
            <div className="p-3 bg-accent text-foreground text-sm rounded-sm" data-testid="no-slots-warning">
              Aucun créneau disponible pour cette date (les réservations doivent être faites au moins 30 minutes à l'avance).
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Commentaire (optionnel)</Label>
            <Textarea data-testid="resa-comment" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Allergies, occasion spéciale, chaise haute…" maxLength={500} className="rounded-sm" rows={3} />
          </div>

          <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:border-primary/60 transition bg-secondary/30">
            <input
              type="checkbox"
              data-testid="resa-marketing-optin"
              checked={form.marketing_optin}
              onChange={(e) => setForm({ ...form, marketing_optin: e.target.checked })}
              className="mt-1 accent-primary shrink-0"
            />
            <span className="text-sm text-foreground leading-relaxed">
              Je souhaite être informé(e) des <strong>offres exclusives</strong> et nouveautés d&apos;Hanamiya Ramen par email (optionnel, désinscription à tout moment).
            </span>
          </label>

          <button type="submit" disabled={submitting || !form.time} data-testid="resa-submit"
            className="w-full bg-primary text-white py-4 rounded-sm hover:bg-primary/90 transition disabled:opacity-60 disabled:cursor-not-allowed">
            {submitting ? "Envoi…" : "Envoyer la demande"}
          </button>

          <p className="text-xs text-muted-foreground text-center">Mardi fermé. Confirmation immédiate par email.</p>
        </form>
      </div>
    </div>
  );
}
